"use client"

import { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import Layout from "../components/Layout"
import Card from "../components/Card"
import Button from "../components/Button"
import Alert from "../components/Alert"
import {
  ArrowLeft,
  Download,
  Eye,
  Check,
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Shield,
  FileText,
  AlertCircle,
} from "react-feather"
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet"
import "leaflet/dist/leaflet.css"
import L from "leaflet"

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
})

const customIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})

const PendingAccount = () => {
  const { userId } = useParams()
  const navigate = useNavigate()
  const [account, setAccount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [downloading, setDownloading] = useState({
    idcard: false,
    legalDocuments: false,
    preview: false,
  })
  const [approving, setApproving] = useState(false)
  const [alert, setAlert] = useState(null)
  const [previewFile, setPreviewFile] = useState(null)
  const [locationCoords, setLocationCoords] = useState(null)
  const API_BASE_URL = 'https://api.tenarx.net';

  // Fetch account details
  const fetchAccountDetails = useCallback(async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("userToken")

      if (!token) {
        navigate("/")
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/admin/pending-account/${userId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch account details")
      }

      const data = await response.json()
      console.log("API Response:", data) // Add this to see the actual structure
      const normalizeFiles = (value) =>
        Array.isArray(value) ? value : value ? [value] : [];
      console.log("Full API response:", data);
console.log("ID cards:", data?.data?.personalInfo?.idcard);
console.log("Legal documents:", data?.data?.personalInfo?.LegalDocuments);

      setAccount({
        ...data.data,
        personalInfo: {
          ...data.data.personalInfo,
          idcard: normalizeFiles(data.data.personalInfo?.idcard),
          LegalDocuments: normalizeFiles(data.data.personalInfo?.LegalDocuments),
        }
      });
    } catch (error) {
      setError(error.message)
      setAlert({
        type: "error",
        message: error.message,
      })
    } finally {
      setLoading(false)
    }
  }, [userId, navigate])

  const handleView = (fileData) => {
    const fileUrl = typeof fileData === "string" ? fileData : fileData?.url
    if (!fileUrl) return

    const filename = fileUrl.split("/").pop()
    const viewUrl = `${API_BASE_URL}/uploads/${filename}`
    setPreviewFile(viewUrl)
  }

  const handleDownload = async (fileData, fileName, fileType) => {
    try {
      setDownloading((prev) => ({ ...prev, [fileType]: true }))

      const fileUrl = typeof fileData === "string" ? fileData : fileData?.url
      if (!fileUrl) throw new Error("Invalid file URL")

      const filename = fileUrl.split("/").pop()

      const response = await fetch(`${API_BASE_URL}/download/${filename}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("userToken")}`,
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = downloadUrl
      link.setAttribute("download", fileName)
      document.body.appendChild(link)
      link.click()

      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
        setDownloading((prev) => ({ ...prev, [fileType]: false }))
      }, 100)
    } catch (error) {
      console.error("Download error:", error)
      setDownloading((prev) => ({ ...prev, [fileType]: false }))
      setAlert({
        type: "error",
        message: `Download failed: ${error.message}`,
      })
    }
  }

  const handleAdminAction = async (action) => {
    try {
      setApproving(true)
      setAlert(null)

      const token = localStorage.getItem("userToken")
      if (!token) {
        navigate("/")
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/admin/approve/${userId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          approve: action === "approve", // true for approve, false for reject
          comments: action === "reject" ? "Account rejected by admin" : "",
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `${action} failed`)
      }

      await fetchAccountDetails()
      setAlert({
        type: "success",
        message: `Account ${action}d successfully!`,
      })
    } catch (error) {
      console.error(`${action} error:`, error)
      setAlert({
        type: "error",
        message: error.message,
      })
    } finally {
      setApproving(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (!window.confirm("Are you sure you want to delete this account? This action cannot be undone.")) return;

    try {
      const token = localStorage.getItem("userToken");
      if (!token) {
        navigate("/");
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/admin/delete-account/${userId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Account deletion failed");
      }

      setAlert({ type: "success", message: "Account deleted successfully." });
      setTimeout(() => navigate(-1), 2000); // Redirect after deletion
    } catch (error) {
      console.error("Delete account error:", error);
      setAlert({ type: "error", message: error.message });
    }
  };

  useEffect(() => {
    fetchAccountDetails()
  }, [fetchAccountDetails])

  useEffect(() => {
    if (account?.personalInfo?.locationLink) {
      const coords = parseLocationCoords(account.personalInfo.locationLink)
      setLocationCoords(coords)
    }
  }, [account])

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "pending":
      case "pending_approval":
        return "bg-yellow-100 text-yellow-800"
      case "active":
      case "approved":
        return "bg-green-100 text-green-800"
      case "rejected":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  // Update parseLocationCoords to extract address if present
  const parseLocationCoords = (locationLink) => {
    if (!locationLink) return null

    try {
      // Check if it's a URL with lat/lng parameters
      if (locationLink.includes("lat=") && locationLink.includes("lng=")) {
        const urlParams = new URLSearchParams(locationLink.split("?")[1])
        const lat = Number.parseFloat(urlParams.get("lat"))
        const lng = Number.parseFloat(urlParams.get("lng"))
        const address = urlParams.get("address") || ""

        if (!isNaN(lat) && !isNaN(lng)) {
          return { lat, lng, address }
        }
      }

      // Check if it's an OpenStreetMap URL format
      if (locationLink.includes("openstreetmap.org") && locationLink.includes("#map=")) {
        const mapPart = locationLink.split("#map=")[1]
        const coords = mapPart.split("/")
        if (coords.length >= 3) {
          const lat = Number.parseFloat(coords[1])
          const lng = Number.parseFloat(coords[2])
          const address = locationLink.split("?query=")[1]?.split("&")[0] || ""

          if (!isNaN(lat) && !isNaN(lng)) {
            return { lat, lng, address: decodeURIComponent(address) }
          }
        }
      }
    } catch (error) {
      console.error("Error parsing location coordinates:", error)
    }

    return null
  }

  const renderFileItem = (file, index, fileType) => {
    // Handle both string paths and file objects
    const fileName = typeof file === "string" 
      ? file.split("/").pop() 
      : file?.name || file?.originalname || `${fileType}-${index + 1}`;
    
    const fileUrl = typeof file === "string" 
      ? `${API_BASE_URL}/uploads/personal-info/${file.split("/").pop()}`
      : file.url || (file.path ? `${API_BASE_URL}/uploads/personal-info/${file.path.split("/").pop()}` : "");

    return (
      <div key={index} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium truncate">{fileName}</span>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => window.open(fileUrl, '_blank')}
            className="flex items-center gap-1"
          >
            <Eye className="w-3 h-3" />
            View
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const link = document.createElement('a');
              link.href = fileUrl;
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            className="flex items-center gap-1"
          >
            <Download className="w-3 h-3" />
            Download
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Layout title="Account Details">
      {alert && (
        <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <div className="flex justify-between items-center mb-6">
        <Button variant="secondary" onClick={() => navigate(-1)} className="flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        {account && account.user && (
          <div className="flex gap-2">
            {account.user.accountStatus === "pending_approval" && (
              <>
                <Button
                  variant="success"
                  onClick={() => handleAdminAction("approve")}
                  disabled={approving}
                  className="flex items-center gap-1"
                >
                  {approving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Approve
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleAdminAction("reject")}
                  disabled={approving}
                  className="flex items-center gap-1"
                >
                  {approving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                  Reject
                </Button>
              </>
            )}
            <Button
              variant="danger"
              onClick={handleDeleteAccount}
              className="flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Delete Account
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <Alert type="error">{error}</Alert>
      ) : account ? (
        <>
          <div className="grid grid-cols-1 gap-6 mb-6">
            <Card>
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex items-center gap-4 mb-4 md:mb-0">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">
                      {account.personalInfo
                        ? `${account.personalInfo.firstName} ${account.personalInfo.lastName}`
                        : account.user.email}
                    </h2>
                    <div className="flex items-center gap-1 mt-1">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusBadgeClass(account.user.accountStatus)}`}
                      >
                        {account.user.accountStatus?.replace("_", " ")}
                      </span>
                      <span className="px-2 py-1 text-xs font-semibold rounded-full capitalize bg-blue-100 text-blue-800">
                        {account.user.role}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>Account created: {new Date(account.user.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Card title="User Information">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Mail className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500">Email</div>
                    <div>{account.user.email}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <Shield className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500">Role</div>
                    <div className="capitalize">{account.user.role}</div>
                  </div>
                </div>

                {account.personalInfo && (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-full">
                        <Phone className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-500">Phone</div>
                        <div>{account.personalInfo.phone || "Not provided"}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-yellow-100 rounded-full">
                        <MapPin className="w-4 h-4 text-yellow-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-500">Country</div>
                        <div>{account.personalInfo.country || "Not provided"}</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Card>

            {account.personalInfo && (
              <Card title="Company Details">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Company Name</p>
                    <p className="mt-1">{account.personalInfo.company || "Not provided"}</p>
                  </div>

                  {account.personalInfo.locationLink && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">Location</p>
                      <a
                        href={account.personalInfo.locationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-1 text-blue-600 hover:text-blue-800"
                      >
                        <MapPin className="w-4 h-4" />
                        View on Map
                      </a>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>

          <Card title="Documentation" className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ID Cards Section */}
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-medium">Identification Documents</h3>
                </div>

                {account?.personalInfo?.idcard ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">Personal identification documents provided by the user.</p>
                    <div className="space-y-2">
                      {Array.isArray(account.personalInfo.idcard) ? (
                        account.personalInfo.idcard.map((file, index) => renderFileItem(file, index, "idcard"))
                      ) : (
                        renderFileItem(account.personalInfo.idcard, 0, "idcard")
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-md">
                    <AlertCircle className="w-5 h-5" />
                    <p className="text-sm">No ID documents uploaded</p>
                  </div>
                )}
              </div>

              {/* Legal Documents Section */}
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-full">
                    <FileText className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium">Legal Documents</h3>
                </div>

                {account?.personalInfo?.LegalDocuments ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">Business registration documents, licenses, and permits.</p>
                    <div className="space-y-2">
                      {Array.isArray(account.personalInfo.LegalDocuments) ? (
                        account.personalInfo.LegalDocuments.map((file, index) => renderFileItem(file, index, "legalDocuments"))
                      ) : (
                        renderFileItem(account.personalInfo.LegalDocuments, 0, "legalDocuments")
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-md">
                    <AlertCircle className="w-5 h-5" />
                    <p className="text-sm">No legal documents uploaded</p>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {account.personalInfo && account.personalInfo.locationLink && (
            <Card title="Location Information" className="mb-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Selected Location</p>
                    {locationCoords && (
                      <div className="space-y-1 mt-1">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Coordinates:</span> {locationCoords.lat.toFixed(6)}, {locationCoords.lng.toFixed(6)}
                        </p>
                        {locationCoords.address && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Address:</span> {locationCoords.address}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <a
                    href={account.personalInfo.locationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <MapPin className="w-4 h-4" />
                    View on OpenStreetMap
                  </a>
                </div>

                {locationCoords ? (
                  <div className="h-[300px] rounded-lg overflow-hidden border border-gray-300 shadow-sm">
                    <MapContainer
                      center={[locationCoords.lat, locationCoords.lng]}
                      zoom={15}
                      style={{ height: "100%", width: "100%" }}
                      scrollWheelZoom={false}
                      dragging={false}
                      zoomControl={false}
                      doubleClickZoom={false}
                      attributionControl={true}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={[locationCoords.lat, locationCoords.lng]} icon={customIcon}>
                        {locationCoords.address && (
                          <Popup>
                            <div className="text-sm">
                              <p className="font-medium">Location Details</p>
                              <p>{locationCoords.address}</p>
                              <p className="mt-1 text-xs">
                                Coordinates: {locationCoords.lat.toFixed(6)}, {locationCoords.lng.toFixed(6)}
                              </p>
                            </div>
                          </Popup>
                        )}
                      </Marker>
                    </MapContainer>
                  </div>
                ) : (
                  <div className="h-[300px] bg-gray-100 rounded-lg flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <MapPin className="w-8 h-8 mx-auto mb-2" />
                      <p>Unable to parse location coordinates</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          <Card title="Account Status">
            {account.user.accountStatus === "pending_approval" ? (
              <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg">
                <div className="p-2 bg-yellow-100 rounded-full">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-medium">Pending Approval</h3>
                  <p className="text-sm text-gray-600">This account is waiting for admin review and approval.</p>
                </div>
              </div>
            ) : account.user.accountStatus === "active" ? (
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                <div className="p-2 bg-green-100 rounded-full">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-medium">Account Approved</h3>
                  <p className="text-sm text-gray-600">This account has been reviewed and approved.</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
                <div className="p-2 bg-red-100 rounded-full">
                  <X className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-medium">Account Rejected</h3>
                  <p className="text-sm text-gray-600">
                    This account has been rejected and cannot access the platform.
                  </p>
                </div>
              </div>
            )}
          </Card>
        </>
      ) : (
        <div className="text-center text-gray-500 py-12">No account data found</div>
      )}

      {/* Document Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">Document Preview</h3>
              <button onClick={() => setPreviewFile(null)} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4">
              <div className="flex justify-center">
                {previewFile.endsWith(".pdf") ? (
                  <iframe src={previewFile} className="w-full h-[70vh]" title="PDF Preview"></iframe>
                ) : (
                  <img
                    src={previewFile || "/placeholder.svg"}
                    alt="Document preview"
                    className="max-w-full max-h-[70vh] object-contain"
                  />
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="secondary" onClick={() => setPreviewFile(null)}>
                Close
              </Button>
              <Button
                onClick={() => {
                  const filename = previewFile.split("/").pop()
                  handleDownload(filename, filename, "preview")
                }}
              >
                <Download className="w-4 h-4 mr-1" />
                Download
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default PendingAccount
